import React, { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { useGetMachineIdTranslator } from "../../hooks/useGetMachineIdTranslator";
import { useUpdateMachineIdTranslator } from "../../hooks/useUpdateMachineIdTranslator";
import Form from "../../interface/Form";
import FormRow from "../../interface/FormRow";
import Input from "../../interface/Input";
import ButtonNeu from "../../interface/ButtonNeu";
import { TiDeleteOutline } from "react-icons/ti";
import { IoAddCircleOutline } from "react-icons/io5";
import styled from "styled-components";
import HeadingSection from "../maintenance/HeadingSection";

const Container = styled.div`
  display: flex;
  width: fit-content;
  min-height: 1rem;
  padding: 1rem;
  background-color: ${(props) =>
    props.$type === "current"
      ? "var(--backdrop-color-ex)"
      : "var(--backdrop-color)"};
  backdrop-filter: blur(10px);
  box-shadow: var(--shadow-div-md);
  border-radius: 1rem;
  margin-bottom: 1.5rem;
  margin-top: 1rem;
  align-items: flex-start;
`;

const ContainerAssets = styled.div`
  margin-top: 1rem;
  margin-bottom: 1rem;
`;

const ContainerBtnUpdate = styled.div`
  display: flex;
  margin-top: 1rem;
  align-self: center;
`;

const StyledHeadingSection = styled.h5`
  color: var(--color-grey-500);
`;

const ImageButton = styled.button`
  margin: auto;
  width: fit-content;
  padding: 0.35rem;
  border-radius: 50%;
  color: var(--color-red-800);
  background-color: var(--color-grey-50);
  border: 1px solid var(--color-red-800);
  &:hover {
    background-color: var(--color-grey-500);
  }
`;

function UpdateMachineInfoMain() {
  const { register, handleSubmit, setValue, control, reset } = useForm();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "witoolId",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBwosId, setSelectedBwosId] = useState(() =>
    searchParams.get("machine")
  );

  const { data: machineListData, isLoading: isMachineListLoading } =
    useGetMachineIdTranslator({});
  const { data, isLoading, isError } = useGetMachineIdTranslator({
    machine: selectedBwosId,
  });
  const { mutate: updateMachineFaults } = useUpdateMachineIdTranslator();

  // Handle bwosId selection from dropdown
  const handleBwosIdChange = (e) => {
    const newBwosId = e.target.value;
    setSelectedBwosId(newBwosId);
    setSearchParams({ machine: newBwosId });
  };

  useEffect(() => {
    if (isLoading || isError) {
      reset();
    }
  }, [isLoading, isError, reset]);

  useEffect(() => {
    if (data?.data && data.data.length > 0 && !isLoading) {
      const machineData = data.data[0];
      setValue("bwosId", machineData.bwosId);
      setValue("labelShort", machineData.labelShort);
      setValue("handler", machineData.handler);
      setValue("kpi1", machineData.kpi1);
      setValue("kpi2", machineData.kpi2);
      setValue("kpi3", machineData.kpi3);

      const witoolIdArray = (() => {
        try {
          return machineData.witoolId ? JSON.parse(machineData.witoolId) : [];
        } catch (error) {
          console.error("Invalid JSON string for witoolId:", error);
          return [];
        }
      })();

      reset({ witoolId: witoolIdArray });
    }
  }, [data, isLoading, reset, setValue]);

  const onSubmit = (formData) => {
    const updateData = {
      ...formData,
      bwosId: selectedBwosId,
      witoolId: JSON.stringify(formData.witoolId),
    };

    updateMachineFaults(updateData);
  };

  return (
    <>
      <HeadingSection text="KPI and WiTool Settings" />
      <Container>
        <Form type="modal" onSubmit={handleSubmit(onSubmit)}>
          <FormRow label="Machine">
            <select value={selectedBwosId || ""} onChange={handleBwosIdChange}>
              <option value="" disabled>
                Select a Machine
              </option>
              {!isMachineListLoading && machineListData?.data.length > 0
                ? machineListData.data.map((machine) => (
                    <option key={machine.bwosId} value={machine.bwosId}>
                      {machine.bwosId}
                    </option>
                  ))
                : []}
            </select>
          </FormRow>

          <FormRow label="Short Label">
            <Input
              type="text"
              {...register("labelShort", { required: true })}
            />
          </FormRow>
          <FormRow label="Handler">
            <Input type="text" {...register("handler")} />
          </FormRow>
          <FormRow label="KPI 1">
            <Input type="number" step="0.1" {...register("kpi1")} />
          </FormRow>
          <FormRow label="KPI 2">
            <Input type="number" step="0.1" {...register("kpi2")} />
          </FormRow>
          <FormRow label="KPI 3">
            <Input type="number" step="0.1" {...register("kpi3")} />
          </FormRow>

          <ContainerAssets>
            <StyledHeadingSection>WITOOL ASSETS</StyledHeadingSection>
            {fields.map((field, index) => (
              <ContainerAssets
                key={field.id}
                style={{ display: "flex", alignItems: "center" }}
              >
                <FormRow label={`${index + 1}`}>
                  <Input
                    type="text"
                    {...register(`witoolId.${index}.description`)}
                    placeholder="Description"
                  />
                  <Input
                    type="text"
                    {...register(`witoolId.${index}.id`, { required: true })}
                    placeholder="ID"
                  />

                  <ImageButton onClick={() => remove(index)}>
                    <TiDeleteOutline />
                  </ImageButton>
                </FormRow>
              </ContainerAssets>
            ))}
            <ButtonNeu
              type="button"
              onClick={() => append({ id: "", description: "" })}
            >
              <IoAddCircleOutline />
            </ButtonNeu>
          </ContainerAssets>
          <ContainerBtnUpdate>
            <ButtonNeu type="submit">Update Machine Info</ButtonNeu>
          </ContainerBtnUpdate>
        </Form>
      </Container>
    </>
  );
}

export default UpdateMachineInfoMain;
